import {Banner} from '@primer/react'
import {PlaygroundCard} from '../../../components/PlaygroundCard'
import {PublisherAvatar} from '../../../components/PublisherAvatar'
import type {ModelInputSchema, ModelState} from '../../../types'
import {testIdProps} from '@github-ui/test-id-props'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import type {Model} from '@github-ui/marketplace-common'

import styles from './PlaygroundChatEmptyState.module.css'

interface Props {
  model: ModelState
  submitMessage: (s: string) => void
}

export default function PlaygroundChatEmptyState({model, submitMessage}: Props) {
  const {catalogData, modelInputSchema} = model
  const iconSize = {narrow: 32, regular: 40, wide: 40}

  return (
    <>
      <UnstableModelBanner name={catalogData.name} friendly_name={catalogData.friendly_name} />
      <div className="d-flex height-full flex-column flex-justify-center">
        <div className="d-flex flex-row flex-justify-center tmp-pb-3">
          <PublisherAvatar
            logoUrl={catalogData.logo_url}
            darkModeIcon={catalogData.dark_mode_icon}
            publisher={catalogData.publisher}
            size={iconSize}
          />
        </div>
        <h3 className={styles.friendlyNameText}>{catalogData.friendly_name}</h3>
        <p className={styles.summaryText}>{catalogData.summary}</p>
        <ModelSampleInputs modelInputSchema={modelInputSchema} submitMessage={submitMessage} />
      </div>
    </>
  )
}

function UnstableModelBanner({name, friendly_name}: Pick<Model, 'name' | 'friendly_name'>) {
  const formattedModelName = name.toLowerCase().replace(/[-\s]/g, '_')
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const isModelUnstable = useFeatureFlag(`github_models_${formattedModelName}_unstable`)
  if (!isModelUnstable) return null
  return (
    <Banner
      {...testIdProps('unstable-model-warning-banner')}
      variant="warning"
      title="Unstable model"
      hideTitle
      className="width-full tmp-mb-3"
    >
      We&apos;re aware that {friendly_name} is experiencing degraded performance, and our team is actively investigating
      the issue. We appreciate your patience as we work toward a resolution.
    </Banner>
  )
}

function ModelSampleInputs({
  modelInputSchema,
  submitMessage,
}: Pick<Props, 'submitMessage'> & {modelInputSchema: ModelInputSchema}) {
  if (!modelInputSchema || !modelInputSchema.sampleInputs || modelInputSchema.sampleInputs.length < 1) return null

  return (
    <div {...testIdProps('sample-inputs')} aria-label="Suggested prompts" className={styles.sampleInputContainer}>
      {modelInputSchema.sampleInputs.slice(0, 3).map(({messages}, index) => (
        <ModelSampleInput
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={`${messages?.[0]?.content}-${index}`}
          messages={messages}
          submitMessage={submitMessage}
        />
      ))}
    </div>
  )
}

function ModelSampleInput({
  messages,
  submitMessage,
}: Pick<Props, 'submitMessage'> & {
  messages?: Array<{content?: string | undefined}> | undefined
}) {
  const content = messages && messages[0] && messages[0].content ? messages[0].content : null
  if (!content) return null

  return (
    <PlaygroundCard
      size={{narrow: 'small', regular: 'large'}}
      sample={content}
      onAction={() => submitMessage(content)}
    />
  )
}
